import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  TicketIcon, Search, Filter, Clock, AlertTriangle, CheckCircle2, 
  User, Building2, RefreshCw, Plus
} from 'lucide-react';

export default function TicketsList() {
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState({});
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    category: 'all',
    search: ''
  });

  useEffect(() => {
    fetchCategories();
    fetchTickets();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/tickets/categories`);
      const data = await res.json();
      setCategories(data.categories || {});
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.priority && filters.priority !== 'all') params.append('priority', filters.priority);
      if (filters.category && filters.category !== 'all') params.append('category', filters.category);

      const res = await fetch(`${API}/tickets?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setTickets(data.tickets || []);
    } catch (error) {
      toast.error('Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchTickets();
    }, 300);
    return () => clearTimeout(debounce);
  }, [filters.status, filters.priority, filters.category]);

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700 border-blue-200',
      in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
      pending_customer: 'bg-purple-100 text-purple-700 border-purple-200',
      resolved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      closed: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    return <Badge className={`${styles[status] || 'bg-slate-100'} border`}>{status?.replace('_', ' ')}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-slate-50 text-slate-600 border-slate-200',
      medium: 'bg-blue-50 text-blue-600 border-blue-200',
      high: 'bg-orange-50 text-orange-600 border-orange-200',
      critical: 'bg-red-50 text-red-600 border-red-200'
    };
    return <Badge className={`${styles[priority] || 'bg-slate-50'} border`}>{priority}</Badge>;
  };

  const getSlaIndicator = (ticket) => {
    const slaStatus = ticket.sla_status;
    if (!slaStatus) return null;

    if (slaStatus.resolution === 'breached' || slaStatus.first_response === 'breached') {
      return <AlertTriangle className="h-4 w-4 text-red-500" title="SLA Breached" />;
    }
    if (slaStatus.resolution === 'met') {
      return <CheckCircle2 className="h-4 w-4 text-emerald-500" title="SLA Met" />;
    }
    if (typeof slaStatus.resolution === 'string' && slaStatus.resolution.includes('remaining')) {
      return <Clock className="h-4 w-4 text-amber-500" title={slaStatus.resolution} />;
    }
    return null;
  };

  const filteredTickets = tickets.filter(t => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        t.ticket_number?.toLowerCase().includes(search) ||
        t.subject?.toLowerCase().includes(search) ||
        t.customer_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    breached: tickets.filter(t => t.sla_status?.resolution === 'breached').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Support Tickets</h1>
          <p className="text-slate-500">Manage customer support requests</p>
        </div>
        <Button onClick={() => navigate('/tickets/new')} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="mr-2 h-4 w-4" /> New Ticket
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <TicketIcon className="h-8 w-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600">Open</p>
                <p className="text-2xl font-bold text-blue-700">{stats.open}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600">In Progress</p>
                <p className="text-2xl font-bold text-amber-700">{stats.inProgress}</p>
              </div>
              <User className="h-8 w-8 text-amber-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600">SLA Breached</p>
                <p className="text-2xl font-bold text-red-700">{stats.breached}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search tickets..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
              />
            </div>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="pending_customer">Pending Customer</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
              <SelectTrigger className="w-full sm:w-32">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(categories).map(([key, cat]) => (
                  <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={fetchTickets}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tickets List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading tickets...</div>
          ) : filteredTickets.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <TicketIcon className="mx-auto h-12 w-12 text-slate-300 mb-3" />
              <p>No tickets found</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredTickets.map((ticket) => (
                <Link
                  key={ticket.ticket_id}
                  to={`/tickets/${ticket.ticket_id}`}
                  className="block p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-mono text-xs text-slate-400">{ticket.ticket_number}</span>
                        {getPriorityBadge(ticket.priority)}
                        {getSlaIndicator(ticket)}
                      </div>
                      <p className="font-medium text-slate-900 truncate">{ticket.subject}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {ticket.customer_name || 'Unknown'}
                        </span>
                        {ticket.assigned_to_name && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {ticket.assigned_to_name}
                          </span>
                        )}
                        <span>
                          {new Date(ticket.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {getStatusBadge(ticket.status)}
                      <span className="text-xs text-slate-400">
                        {categories[ticket.category]?.label || ticket.category}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
